import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { ComponentType, useCallback, useEffect, useState } from 'react'
import React from 'react'
import { useLocation, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { twMerge as twm } from 'tailwind-merge'

import { useErrorHandler } from '../hooks/useErrorHandler'
import { User } from '../models/User'
import { PocketComponentProgressBarStateProvider } from '../providers/PocketComponentProgressBarStateProvider'
import { Spinner } from './Spinner'
import { PSApplication } from './app/PSApplication'

export type PocketComponent<Props = any> = {
    showProgressBar: (text?: string) => void
    hideProgressBar: () => void
    logAndDisplayError: (error: any) => void
    urlParams: any
    pathName: string
    locationState: any
    searchParams: URLSearchParams
    props: Props
    reload: () => void
    me: User
    owner: User
    queryClient: QueryClient
}

export type PocketComponentProps<Data, Props = any> = {
    data: Data // response from preloader
    base: PocketComponent<Props> // base class to get access to base component methods
}

/** @deprecated - use react router loaders or react query instead */
export function usePSComponent<Props = any>(
    WrappedComponent: ComponentType<PocketComponentProps<any, Props>>,
    viewWillAppear?: (base: PocketComponent) => Promise<any>
) {
    const WrapperComponent: ComponentType<Props> = (props: Props) => {
        const params = useParams()
        const location = useLocation()
        const [searchParams] = useSearchParams()
        const { logAndDisplayError } = useErrorHandler()
        const queryClient = useQueryClient()

        const [initializationState, setInitializationState] = useState<
            { type: 'initializing' } | { type: 'initialized'; data: any; base: PocketComponent }
        >({ type: 'initializing' })

        const [loadingState, setLoadingState] = useState<
            { type: 'idle' } | { type: 'loading'; loadingText: string | null }
        >({ type: 'idle' })

        const [shouldShowSpinner, setShouldShowSpinner] = useState(false)

        const showProgressBar = useCallback(
            (text?: string) => setLoadingState({ type: 'loading', loadingText: text ?? null }),
            []
        )
        // This setTimeout is here for a very hacky reason: we want to delay the rerendering of this
        // component by 1 tick after setting the loading state back to idle so that any <NavigationBlockerPrompt>
        // components in the tree below have a chance to read the state as "loading" still, so that
        // they can prevent themselves from showing while we're loading.
        const hideProgressBar = useCallback(
            () => setTimeout(() => setLoadingState({ type: 'idle' })),
            []
        )

        // Only show the spinner if we've been loading for some small amount of time to prevent flashing
        // if the loading ends quickly.
        useEffect(() => {
            const isLoading =
                loadingState.type === 'loading' || initializationState.type === 'initializing'

            if (!isLoading) {
                setShouldShowSpinner(false)
                return
            }

            const timeoutID = setTimeout(() => setShouldShowSpinner(true), 200)
            return () => {
                clearTimeout(timeoutID)
            }
        }, [loadingState, initializationState.type])

        const reloadComponent = useCallback(async () => {
            setInitializationState({ type: 'initializing' })
        }, [])

        const generateBase = useCallback(() => {
            return {
                showProgressBar,
                hideProgressBar,
                logAndDisplayError,
                urlParams: { ...params },
                pathName: location.pathname,
                locationState: { ...location.state },
                searchParams: searchParams,
                props: { ...props },
                reload: reloadComponent,
                me: PSApplication.me(),
                owner: PSApplication.me().owner,
                queryClient,
            }
        }, [
            showProgressBar,
            hideProgressBar,
            params,
            location,
            props,
            reloadComponent,
            queryClient,
            searchParams,
        ])

        // If the current state is `initializing`, then initialize with the data from viewWillAppear.
        useEffect(() => {
            let isMounted = true

            const initialize = async () => {
                if (initializationState.type === 'initialized') {
                    return
                }

                const base = generateBase()

                if (viewWillAppear === undefined) {
                    setInitializationState({ type: 'initialized', data: null, base })
                }
                else {
                    const data = await viewWillAppear(base)

                    if (!isMounted) {
                        return
                    }

                    setInitializationState({
                        type: 'initialized',
                        data,
                        base,
                    })
                }
            }

            initialize()

            return () => {
                isMounted = false
            }
        }, [initializationState, generateBase])

        // If the path changes, re-initialize.
        useEffect(() => {
            if (initializationState.type === 'initializing') {
                return
            }
            if (initializationState.base.pathName === location.pathname) {
                return
            }
            setInitializationState({ type: 'initializing' })
        }, [initializationState, location.pathname, searchParams])

        // If the base changes, set a new base.
        useEffect(() => {
            setInitializationState((previousState) =>
                previousState.type === 'initializing'
                    ? previousState
                    : {
                          type: 'initialized',
                          data: previousState.data,
                          base: generateBase(),
                      }
            )
        }, [generateBase])

        return (
            <PocketComponentProgressBarStateProvider
                isProgressBarActive={
                    loadingState.type === 'loading' || initializationState.type === 'initializing'
                }
            >
                <div className="relative max-w-full min-w-0">
                    {initializationState.type === 'initialized' && (
                        <div className={twm(shouldShowSpinner && 'opacity-50')}>
                            <WrappedComponent
                                data={initializationState.data}
                                base={initializationState.base}
                            />
                        </div>
                    )}

                    {shouldShowSpinner && (
                        <div className="fixed inset-0 flex h-full items-center justify-center">
                            <div
                                className={twm(
                                    'flex flex-col items-center justify-center gap-y-2',
                                    loadingState.type === 'loading' &&
                                        loadingState.loadingText !== null &&
                                        'rounded-md bg-black/50 p-8'
                                )}
                            >
                                <Spinner />
                                {loadingState.type === 'loading' &&
                                    loadingState.loadingText !== null && (
                                        <span className="text-xl font-bold text-white">
                                            {loadingState.loadingText}
                                        </span>
                                    )}
                            </div>
                        </div>
                    )}
                </div>
            </PocketComponentProgressBarStateProvider>
        )
    }

    return WrapperComponent
}
